// Theme — web twin of the mobile app's palette in contexts/GlobalState.tsx.
// Three-way preference ('system' | 'dark' | 'light') persisted under the same
// key name as mobile ('themePreference'). The resolved mode is stamped onto
// <html data-theme="…">, which is what the CSS custom properties in index.css
// key off; the JS palette below exists for the few places that need colors
// outside CSS (charts).

import type { ReactElement, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { prefersReducedMotion } from './a11y';
import { BRAND_ACCENT_DARK, BRAND_ACCENT_LIGHT } from './colors';

export type ThemePreference = 'system' | 'dark' | 'light';
export type ThemeMode = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'themePreference';

export type Palette = {
  mode: ThemeMode;
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  divider: string;
  fieldUnderline: string;
  accent: string;
  placeholder: string;
  iconBubbleBg: string;
  segmentTrack: string;
  sheetBg: string;
};

// Values copied from the mobile app — keep in sync.
export const DARK_PALETTE: Palette = {
  mode: 'dark',
  pageBg: '#032034',
  cardBg: '#0E2C4F',
  cardBorder: 'rgba(125,184,232,0.2)',
  textPrimary: '#ffffff',
  textSecondary: 'rgba(255,255,255,0.75)',
  textMuted: 'rgba(255,255,255,0.5)',
  divider: 'rgba(255,255,255,0.1)',
  fieldUnderline: 'rgba(255,255,255,0.2)',
  accent: BRAND_ACCENT_DARK, // brand water blue
  placeholder: 'rgba(255,255,255,0.35)',
  iconBubbleBg: '#0E2C4F',
  segmentTrack: 'rgba(255,255,255,0.06)',
  sheetBg: '#0E2C4F',
};

export const LIGHT_PALETTE: Palette = {
  mode: 'light',
  pageBg: '#DCE7F0',
  cardBg: '#ffffff',
  cardBorder: 'transparent',
  textPrimary: '#0B2740',
  textSecondary: '#3D4F62',
  textMuted: '#7B8A9C',
  divider: '#C9D6E2',
  fieldUnderline: '#C9D6E2',
  accent: BRAND_ACCENT_LIGHT, // brand main (deep) blue
  placeholder: '#A6B4C2',
  iconBubbleBg: '#ffffff',
  segmentTrack: 'rgba(11,39,64,0.08)',
  sheetBg: '#ffffff',
};

type ThemeContextValue = {
  preference: ThemePreference;
  mode: ThemeMode;
  palette: Palette;
  setPreference: (p: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  preference: 'system',
  mode: 'dark',
  palette: DARK_PALETTE,
  setPreference: () => {},
});

function readStoredPreference(): ThemePreference {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

function systemMode(): ThemeMode {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [sysMode, setSysMode] = useState<ThemeMode>(systemMode);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => setSysMode(systemMode());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const mode: ThemeMode = preference === 'system' ? sysMode : preference;

  // Layout effect: revealTheme's view transition snapshots the page as soon as
  // its flushSync commit returns, so the attribute must land inside that commit.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;
    // Keep the browser chrome (mobile address bar) in step with the page.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', mode === 'dark' ? DARK_PALETTE.pageBg : LIGHT_PALETTE.pageBg);
  }, [mode]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, p);
    } catch {
      /* best effort */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      mode,
      palette: mode === 'dark' ? DARK_PALETTE : LIGHT_PALETTE,
      setPreference,
    }),
    [preference, mode, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

// Same switch effect as the divako.io site: the new theme grows as a circle out
// of `origin` over the old one (View Transitions API). Plain instant switch
// where the API is missing or motion is reduced.
export function revealTheme(origin: Element, apply: () => void): void {
  if (typeof document.startViewTransition !== 'function' || prefersReducedMotion()) {
    apply();
    return;
  }
  const r = origin.getBoundingClientRect();
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
  const transition = document.startViewTransition(() => flushSync(apply));
  transition.ready
    .then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        {
          duration: 900,
          easing: 'cubic-bezier(.4,0,.2,1)',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    })
    .catch(() => {});
}
